import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import DOMPurify from "dompurify";
import { extractPolicyRecord, getPolicy } from ".";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AdminPageLayout, AdminSectionCard } from "../../components/admin/AdminPageLayout";
import { AdminAction } from "../../components/admin";

export default function PrivacyPolicy() {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { results } = location.state || {};

  useEffect(() => {
    if (results) {
      toast.success("Policy updated successfully!", {
        toastId: "success1",
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      // Clear one-time success state so refresh/nav doesn't re-toast.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  const navigateToUpdatePage = () => {
    navigate(`/privacypolicy/update`);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getPolicy();
      setPolicy(extractPolicyRecord(response.data));
    } catch (error) {
      console.log(error);
      setPolicy(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const sanitizedHtml = useMemo(() => {
    const raw = policy?.policy;
    if (!raw) return "";
    return DOMPurify.sanitize(raw, {
      USE_PROFILES: { html: true },
    });
  }, [policy]);

  return (
    <AdminPageLayout
      eyebrow="Policy & Compliance"
      title="Privacy Policy"
      description=""
      actions={
        <div className="admin-toolbar">
          <AdminAction onClick={navigateToUpdatePage}>Click to Edit</AdminAction>
        </div>
      }
    >
      <AdminSectionCard
        title="Current published policy content"
        description=""
      >
        <div className="p-6">
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                py: 8,
                color: "#78716c",
              }}
            >
              <CircularProgress size={22} sx={{ color: "#3f7a2a" }} />
              <span>Loading privacy policy…</span>
            </Box>
          ) : sanitizedHtml ? (
            <div className="prose prose-slate max-w-none">
              <div
                className="text-slate-600 leading-relaxed whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              />
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No privacy policy content found.</p>
          )}
        </div>
      </AdminSectionCard>
    </AdminPageLayout>
  );
}
